export interface ColorTheme {
  defaultBg: string
  selectedFill: string
  selectedFill10: string
  selectedFill15: string
  selectedStroke: string
  avatar: string
  editorTop: string
  overlayColor: string
  editorBg: string
}

/**
 * Converts a hex color string (short or long form) to an rgb object
 * @param hex - The hex color string to convert
 * @returns An object with r, g, and b properties | null
 */

export const hexToRgb = (hex: string) => {
  // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
  var shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i
  hex = hex.replace(shorthandRegex, function (m, r, g, b) {
    return r + r + g + g + b + b
  })

  var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null
}
/**
 * Converts a hex string (short or long form) to an rgba string of given opacity
 * @param hex - The hex color string to convert
 * @param opacity - Opacity value (0-1)
 * @returns rgba string, e.g. rgba(255, 255, 255, 0.1)
 */
export const hexToRgbaString = (hex: string, opacity: number) => {
  const rgb = hexToRgb(hex)
  return `rgba(${rgb?.r}, ${rgb?.g}, ${rgb?.b}, ${opacity})`
}

/**
 * Converts an rgb or rgba string to a hex string. Any alpha channel is ignored,
 * so the returned color is fully opaque.
 * @param rgbString - The rgb/rgba string to convert
 * @returns A capitalized hex string, e.g. #FFFFFF
 */
export const rgbToHex = (rgbString: string): string => {
  const match = rgbString.match(/rgba?\(\s*([^)]+)\)/i)
  const channels = (match ? match[1] : rgbString).split(/[,\s/]+/).filter(Boolean)
  const rgb = channels.slice(0, 3).map((n) => parseInt(n, 10))

  return `#${rgb
    .map((n) => {
      const hex = n.toString(16)
      return hex.length === 1 ? `0${hex}`.toLocaleUpperCase() : hex.toLocaleUpperCase()
    })
    .join('')}`
}

export const getTextColorForBackground = (hexColor: string): 'black' | 'white' => {
  const rgb = hexToRgb(hexColor)
  if (!rgb) return 'white'

  // Calculate relative luminance using sRGB color space
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255
  return luminance > 0.5 ? 'black' : 'white'
}

const hexToHsl = (hex: string) => {
  const rgb = hexToRgb(hex)
  if (!rgb) return { h: 0, s: 0, l: 0 }

  const [r, g, b] = [rgb.r / 255, rgb.g / 255, rgb.b / 255]
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const diff = max - min

  let h = 0
  if (diff !== 0) {
    if (max === r) {
      h = (60 * ((g - b) / diff) + 360) % 360
    } else if (max === g) {
      h = (60 * ((b - r) / diff) + 120) % 360
    } else {
      h = (60 * ((r - g) / diff) + 240) % 360
    }
  }

  const l = (max + min) / 2
  let s = 0
  if (diff !== 0) {
    s = l <= 0.5 ? diff / (max + min) : diff / (2 - max - min)
  }

  return { h: Math.round(h), s: Math.round(s * 100), l: Math.round(l * 100) }
}

export const hslToHex = (h: number, s: number, l: number) => {
  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = l - c / 2

  let r: number, g: number, b: number
  if (h < 60) {
    ;[r, g, b] = [c, x, 0]
  } else if (h < 120) {
    ;[r, g, b] = [x, c, 0]
  } else if (h < 180) {
    ;[r, g, b] = [0, c, x]
  } else if (h < 240) {
    ;[r, g, b] = [0, x, c]
  } else if (h < 300) {
    ;[r, g, b] = [x, 0, c]
  } else {
    ;[r, g, b] = [c, 0, x]
  }

  const toHex = (n: number) =>
    Math.round((n + m) * 255)
      .toString(16)
      .padStart(2, '0')
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase()
}

export const getLuminance = (hexColor: string) => {
  const rgb = hexToRgb(hexColor)
  if (!rgb) return 0

  const toLinear = (c: number) => {
    const v = c / 255
    if (v <= 0.03928) {
      return v / 12.92
    }
    return Math.pow((v + 0.055) / 1.055, 2.4)
  }

  return 0.2126 * toLinear(rgb.r) + 0.7152 * toLinear(rgb.g) + 0.0722 * toLinear(rgb.b)
}

const ensureContrast = (hexColor: string, targetLuminance = 0.3) => {
  let luminance = getLuminance(hexColor)
  if (luminance < targetLuminance) return hexColor

  const { h, s, l } = hexToHsl(hexColor)
  let saturation = Math.min((s / 100) * 1.2, 0.95)
  let lightness = l / 100

  for (let i = 0; i < 15 && luminance >= targetLuminance; i++) {
    lightness *= 0.85
    const newColor = hslToHex(h, saturation, lightness)
    luminance = getLuminance(newColor)
    if (luminance < targetLuminance) return newColor
  }

  return hslToHex(h, Math.max(saturation, 0.85), 0.35)
}

const THEME_CONFIG = {
  background: { lightness: 0.91, saturationMult: 0.55, lightnessBlend: 0.3 },
  selected: { lightness: 0.4, saturationMult: 1.15, maxSat: 0.95, lightnessBlend: 0.5 },
  avatar: { lightness: 0.57, saturationMult: 1.1, maxSat: 0.9, lightnessBlend: 0.4 },
  editorTop: { lightness: 0.7, saturationMult: 1.05, maxSat: 0.95, lightnessBlend: 0.5 },
  editorBg: { lightness: 0.95, saturationMult: 0.22, lightnessBlend: 0.2 },
} as const

export const generateColorTheme = (brandColor: string): ColorTheme => {
  const { h, s: saturation, l: inputLightness } = hexToHsl(brandColor)
  const s = saturation / 100
  const l = inputLightness / 100
  const { background: bg, selected: sel, avatar: av, editorTop: et, editorBg: eb } = THEME_CONFIG

  const blendLightness = (target: number, blend: number, min: number, max: number) => {
    const blended = target * (1 - blend) + l * blend
    return Math.max(min, Math.min(max, blended))
  }

  const defaultBg = hslToHex(h, s * bg.saturationMult, blendLightness(bg.lightness, bg.lightnessBlend, 0.88, 0.95))

  const selectedLightness = blendLightness(sel.lightness, sel.lightnessBlend, 0.25, 0.55)
  let selectedFill = hslToHex(h, Math.min(s * sel.saturationMult, sel.maxSat), selectedLightness)
  if (getLuminance(selectedFill) >= 0.3) {
    selectedFill = ensureContrast(brandColor)
  }

  const bgRgb = hexToRgb(defaultBg)
  const overlayColor = bgRgb ? `${bgRgb.r}, ${bgRgb.g}, ${bgRgb.b}` : '238, 241, 241'

  return {
    defaultBg,
    selectedFill,
    selectedFill10: hexToRgbaString(selectedFill, 0.1),
    selectedFill15: hexToRgbaString(selectedFill, 0.15),
    selectedStroke: selectedFill,
    avatar: hslToHex(
      h,
      Math.min(s * av.saturationMult, av.maxSat),
      blendLightness(av.lightness, av.lightnessBlend, 0.45, 0.7),
    ),
    editorTop: hslToHex(
      h,
      Math.min(s * et.saturationMult, et.maxSat),
      blendLightness(et.lightness, et.lightnessBlend, 0.4, 0.85),
    ),
    overlayColor,
    editorBg: hslToHex(h, s * eb.saturationMult, blendLightness(eb.lightness, eb.lightnessBlend, 0.92, 0.98)),
  }
}
